import { type CarListApiData, IamGroupType, OrganizationType, UserGroupCarRole } from '@navio/nsdk';
import { filterUnitFor, type SharedQuery, useFilterUnits } from 'noddi-ui-web';
import { useCarMakeNameOptions } from '../../hooks/filters/Car/useCarMakeNamesFilter';
import { useCarModelNameOptions } from '../../hooks/filters/Car/useCarModelNamesFilter';
import { useOrganizationOptions } from '../../hooks/filters/Organization/useOrganizationFilter';
import { useUserGroupOptions } from '../../hooks/filters/UserGroup/useUserGroupFilter';
import {
  type FleetViewFilterInput,
  fleetViewFilterUnit,
  fleetViewSummaryFilterSpecs
} from './fleetViewSummaryFilterSpecs';
import { defaultFleetViewFilterTranslations, FleetViewFilterTranslations } from './fleetViewTranslations';

export { fleetViewFilterUnit };

type CarFilterInput = Omit<NonNullable<CarListApiData['query']>, 'pageIndex' | 'pageSize' | 'ordering' | 'search'>;

export const carFilterUnit = filterUnitFor<CarFilterInput>();
/** Make and model feed both the fleet view and the cars endpoint: lists that switch to cars keep them. */
const fleetViewAndCarFilterUnit = filterUnitFor<SharedQuery<FleetViewFilterInput, CarFilterInput>>();

export const useFleetViewTableFilters = (
  translations?: FleetViewFilterTranslations,
  options?: {
    showTireHotelFilters?: boolean;
    showLeasingFilters?: boolean;
  }
) => {
  const t = translations ?? defaultFleetViewFilterTranslations;
  const showTireHotelFilters = options?.showTireHotelFilters ?? true;
  const showLeasingFilters = options?.showLeasingFilters ?? false;
  const summarySpecs = fleetViewSummaryFilterSpecs(t);

  const makeSpec = fleetViewAndCarFilterUnit.multiSelect('makeNames', {
    title: t.carMakes,
    description: t.carMakesDescription,
    defaultVisibleOptions: 5,
    source: useCarMakeNameOptions(),
    exclude: true
  });
  // Registry model names are not scoped to a make, so the model options do not narrow with the make picked.
  const modelSpec = fleetViewAndCarFilterUnit.multiSelect('modelNames', {
    title: t.carModels,
    description: t.carModelsDescription,
    defaultVisibleOptions: 5,
    source: useCarModelNameOptions(),
    exclude: true
  });

  const payers = useUserGroupOptions({ types: [IamGroupType.ORGANIZATION], isInvoicePayer: true });
  const leaseholders = useUserGroupOptions({ carRoles: [UserGroupCarRole.LEASEHOLDER], enabled: showLeasingFilters });
  const leasingCompanies = useOrganizationOptions({
    organizationTypes: [OrganizationType.LEASING_COMPANY],
    enabled: showLeasingFilters
  });
  const carDealerships = useOrganizationOptions({
    organizationTypes: [OrganizationType.CAR_DEALERSHIP],
    enabled: showLeasingFilters
  });

  // Hidden filters write no params, so a stale URL value cannot narrow the list.
  const filters = useFilterUnits([
    ...(showTireHotelFilters
      ? [summarySpecs.inStorageSeason, summarySpecs.tireWearStatusSummer, summarySpecs.tireWearStatusWinter]
      : []),
    summarySpecs.hasBookingItemUpcoming,
    summarySpecs.upcomingBookingDate,
    summarySpecs.lastBookingDate,
    makeSpec,
    modelSpec,
    fleetViewFilterUnit.multiSelect('userGroupPayerIds', {
      title: 'Invoice to',
      description: 'Filter by the organization the car owner is invoiced to (Faktureres til)',
      source: payers,
      defaultVisibleOptions: 3,
      exclude: true
    }),
    fleetViewFilterUnit.boolean('hasInvoice', {
      title: 'Has invoice',
      description: 'Show cars whose current owner group can pay with invoice (Faktura).'
    }),
    ...(showLeasingFilters
      ? [
          fleetViewFilterUnit.boolean('isLeased', {
            title: 'Leased',
            description: 'Show cars that are leased (owned by a leasing company or car dealership).'
          }),
          fleetViewFilterUnit.multiSelect('leaseholderIds', {
            title: 'Leaseholder',
            description: 'Filter by the personal customer leasing the car (leaseholder).',
            source: leaseholders,
            defaultVisibleOptions: 3,
            exclude: true
          }),
          fleetViewFilterUnit.multiSelect('leasingCompanyIds', {
            title: 'Leasing company',
            description: 'Show records whose car is owned by these leasing companies.',
            source: leasingCompanies,
            defaultVisibleOptions: 3,
            exclude: true
          }),
          fleetViewFilterUnit.multiSelect('carDealershipIds', {
            title: 'Car dealership',
            description: 'Show records whose car is associated with these car dealerships (selling dealer).',
            source: carDealerships,
            defaultVisibleOptions: 3,
            exclude: true
          })
        ]
      : [])
  ]);

  const carFilters = useFilterUnits([makeSpec, modelSpec]);

  return { filters, carFilters };
};
